// The Today rail + the morning reconciliation strip (design handoff §6.4/§6.5,
// D1-c §3.2). 392px, sticky, and the only place the day's itinerary exists.
//
// Two rules the room depends on:
//
//   1. **A focus is a pin, not a copy.** Ticking a row here writes through to
//      the register row it names, so the project card shows `Done` immediately.
//   2. **No text input, ever.** A genuine one-off is quick-added into a project
//      register (Support is always a valid home) and then pulled. The absence is
//      deliberate, so it is stated in a footnote rather than left as a gap.
//
// The reconciliation strip offers FOUR outcomes, not the handoff's three. Park
// is Isaac's: work blocked long enough that carrying it daily is noise gets to
// leave Today, stay open on the project, and carry a parked-since date.

import { useState } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import type { Focus, FocusResolution } from "../lib/api";
import { PriorityPill } from "./RegisterTable";

const c = brainTokens.color;

/** Whole days between two YYYY-MM-DD dates. */
export function daysBetween(from?: string, to?: string): number {
  if (!from || !to) return 0;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/** Carry age for a pin: today − firstPulledAt, falling back to its row's date. */
export function carryDays(focus: Focus, rowDate: string, today: string): number {
  return daysBetween(focus.firstPulledAt || rowDate, today);
}

/** A pin that has left Today via Park is not part of the itinerary. */
export function isParked(focus: Focus): boolean {
  return focus.resolvedAs === "park";
}

/** Today's itinerary = today's focuses, minus anything parked out of it. */
export function railItems(focuses: Focus[]): Focus[] {
  return focuses.filter((f) => !isParked(f) && f.resolvedAs !== "return");
}

export interface ReconRow {
  focus: Focus;
  /** The row the pin lives on — yesterday's date, or an older carried one. */
  date: string;
  projectName?: string;
}

/** Group the rail by project, preserving first-seen order. */
export function groupByProject(
  items: Focus[],
  nameOf: (projectId?: string) => string,
): { key: string; label: string; items: Focus[] }[] {
  const out: { key: string; label: string; items: Focus[] }[] = [];
  const index = new Map<string, number>();
  for (const f of items) {
    const key = f.projectId ?? "__none__";
    let at = index.get(key);
    if (at === undefined) {
      at = out.length;
      index.set(key, at);
      out.push({ key, label: f.projectId ? nameOf(f.projectId) : "No project", items: [] });
    }
    out[at].items.push(f);
  }
  return out;
}

export const RESOLUTION_LABEL: Record<FocusResolution, string> = {
  done: "✓ Marked done",
  carry: "↻ Carried into today",
  return: "↩ Returned to register",
  park: "⏸ Parked",
};

function CarryChip({ days }: { days: number }) {
  if (days < 1) return null;
  const loud = days >= 3;
  return (
    <Box
      component="span"
      title={loud ? `Carried ${days} days — decide it or park it` : `Carried ${days} day${days === 1 ? "" : "s"}`}
      sx={{
        whiteSpace: "nowrap",
        fontFamily: brainTokens.font.mono,
        fontSize: 11,
        fontWeight: 700,
        borderRadius: `${brainTokens.radius.pill}px`,
        px: "7px",
        py: "2px",
        color: loud ? c.dangerText : c.warnText,
        background: loud ? c.dangerBg : c.warnBg,
      }}
    >
      carried {days}d
    </Box>
  );
}

function RailButton({
  label,
  tone,
  onClick,
  busy,
  title,
}: {
  label: string;
  tone: "success" | "warning" | "neutral" | "park";
  onClick: () => void;
  busy?: boolean;
  title?: string;
}) {
  const look = {
    success: { fg: c.successText, bg: c.successBg, bd: c.successBorder },
    warning: { fg: c.warnText, bg: c.warnBg, bd: c.warnBorder },
    neutral: { fg: c.ink4, bg: c.white, bd: c.dropBorder },
    park: { fg: c.ink3, bg: c.neutralPillBg, bd: c.border },
  }[tone];
  return (
    <Box
      component="button"
      type="button"
      title={title}
      disabled={busy}
      onClick={onClick}
      sx={{
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: look.fg,
        background: look.bg,
        border: `1px solid ${look.bd}`,
        borderRadius: `${brainTokens.radius.button}px`,
        px: "4px",
        py: "6px",
        cursor: busy ? "default" : "pointer",
        opacity: busy ? 0.55 : 1,
        fontFamily: brainTokens.font.sans,
      }}
    >
      {label}
    </Box>
  );
}

export function ReconciliationStrip({
  rows,
  today,
  resolved,
  onResolve,
  onCarryAll,
  busy,
}: {
  rows: ReconRow[];
  today: string;
  /** focusId → the outcome already recorded this morning. */
  resolved: Record<string, FocusResolution>;
  onResolve: (row: ReconRow, resolution: FocusResolution) => void;
  onCarryAll: () => void;
  busy?: boolean;
}) {
  if (!rows.length) return null;
  const remaining = rows.filter((r) => !resolved[r.focus.id]).length;
  // "The strip disappears when the last is resolved — that is the only 'done'
  // signal needed." (handoff §6.5)
  if (remaining === 0) return null;

  return (
    <Box
      data-testid="reconciliation-strip"
      sx={{
        border: `1px solid ${c.warnBorder}`,
        background: c.warnBg4,
        borderRadius: `${brainTokens.radius.card}px`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: "13px 16px 11px", borderBottom: `1px solid ${c.warnBorder}` }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
          <Box
            component="span"
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.11em",
              color: c.warnText,
              whiteSpace: "nowrap",
            }}
          >
            YESTERDAY · UNRESOLVED
          </Box>
          <Box
            component="span"
            sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.warnText, whiteSpace: "nowrap" }}
          >
            {remaining} OF {rows.length} LEFT
          </Box>
        </Box>
        <Box sx={{ fontSize: 16, fontWeight: 650, color: c.ink, mt: 0.75 }}>
          Dispatch before you pull today
        </Box>
        <Box
          component="button"
          type="button"
          disabled={busy}
          onClick={onCarryAll}
          sx={{
            mt: 1.1,
            fontSize: 12.5,
            fontWeight: 500,
            color: c.ink3,
            background: c.white,
            border: `1px solid ${c.warnBorder}`,
            borderRadius: `${brainTokens.radius.button}px`,
            px: "11px",
            py: "5px",
            cursor: busy ? "default" : "pointer",
            fontFamily: brainTokens.font.sans,
          }}
        >
          Carry all into today
        </Box>
      </Box>

      {/*
        The strip is UNBOUNDED — one row per unresolved pin, and `carried` looks
        back 15 standup days. Aaron's real board opens with 11 rows / 1351px,
        which pushed the Today rail 687px below the fold on a 942px viewport: the
        heart of the person tab was invisible on load, and a sticky column taller
        than the window never sticks. So the rows scroll inside the strip and the
        rail stays on screen. Nothing is hidden — `N OF M LEFT` above says how
        many there are, and every row is still reachable.
      */}
      <Box data-testid="recon-rows" sx={{ maxHeight: "44vh", overflowY: "auto" }}>
      {rows.map((row) => {
        const outcome = resolved[row.focus.id];
        const days = carryDays(row.focus, row.date, today);
        const loud = days >= 3;
        return (
          <Box
            key={row.focus.id}
            sx={{
              p: "12px 16px",
              borderBottom: `1px solid ${c.warnBg3}`,
              opacity: outcome ? 0.62 : 1,
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 30,
                  height: 24,
                  mt: "1px",
                  flex: "none",
                  borderRadius: `${brainTokens.radius.chip}px`,
                  fontFamily: brainTokens.font.mono,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: loud ? c.dangerText : c.warnText,
                  background: loud ? c.dangerBg : c.warnBg,
                  border: `1px solid ${loud ? c.dangerBorder : c.warnBorder}`,
                }}
              >
                {days}d
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    color: outcome ? c.muted2 : c.ink,
                    textDecoration: outcome === "done" ? "line-through" : "none",
                  }}
                >
                  {row.focus.text}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  {row.focus.registerRef || row.focus.issueKey ? (
                    <Box
                      component="span"
                      sx={{ fontFamily: brainTokens.font.mono, fontSize: 10.5, color: c.muted2 }}
                    >
                      {row.focus.registerRef || row.focus.issueKey}
                    </Box>
                  ) : null}
                  {row.projectName && (
                    <Box component="span" sx={{ fontSize: 12, color: c.muted }}>
                      {row.projectName}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {outcome ? (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  mt: 1,
                  fontSize: 12.5,
                  fontWeight: 600,
                  px: "11px",
                  py: "5px",
                  borderRadius: `${brainTokens.radius.pill}px`,
                  whiteSpace: "nowrap",
                  background: outcome === "done" ? c.successBg : outcome === "carry" ? c.warnBg : c.neutralPillBg,
                  color: outcome === "done" ? c.successText : outcome === "carry" ? c.warnText : c.muted,
                }}
              >
                {RESOLUTION_LABEL[outcome]}
                {outcome === "park" ? ` · since ${today.slice(5)}` : ""}
              </Box>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "6px", mt: 1.1 }}>
                <RailButton label="✓ Done" tone="success" busy={busy} onClick={() => onResolve(row, "done")}
                  title="Mark the register row done — it lands in today struck through" />
                <RailButton label="↻ Carry" tone="warning" busy={busy} onClick={() => onResolve(row, "carry")}
                  title="Re-pin onto today; the carry counter increments" />
                <RailButton label="↩ Return" tone="neutral" busy={busy} onClick={() => onResolve(row, "return")}
                  title="Drop the pin; the row stays open on the project" />
                <RailButton label="⏸ Park" tone="park" busy={busy} onClick={() => onResolve(row, "park")}
                  title="Leaves today, stays open on the project, records a parked-since date" />
              </Box>
            )}
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}

export function TodayRail({
  focuses,
  today,
  personName,
  projectName,
  dragging,
  ghost,
  onTick,
  onReturn,
  onDropPin,
  busy,
}: {
  focuses: Focus[];
  today: string;
  personName: string;
  projectName: (projectId?: string) => string;
  /** A row is being dragged over the rail. */
  dragging?: boolean;
  /** The row hovered/dragged — drawn as a dashed teal landing spot. */
  ghost?: { text: string; ref?: string } | null;
  onTick: (focus: Focus, done: boolean) => void;
  onReturn: (focus: Focus) => void;
  onDropPin?: () => void;
  busy?: boolean;
}) {
  const items = railItems(focuses);
  const groups = groupByProject(items, projectName);
  // "5 items · 2 carried" — carried means it entered someone's day BEFORE today.
  // A pin with no `firstPulledAt` is a pre-D1-c focus; treat it as pulled today
  // rather than inventing an age for it.
  const carried = items.filter((f) => carryDays(f, today, today) > 0).length;
  const doneCount = items.filter((f) => f.done).length;
  const allDone = items.length > 0 && doneCount === items.length;

  return (
    <Box
      data-testid="today-rail"
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropPin?.();
      }}
      sx={{
        background: c.white,
        border: `1px solid ${dragging ? c.teal : c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1.25,
          p: "16px 18px 12px",
          borderBottom: `1px solid ${c.borderLight3}`,
        }}
      >
        <Box component="span" sx={{ fontSize: 21, fontWeight: 650, letterSpacing: "-0.01em", color: c.ink }}>
          Today
        </Box>
        <Box
          component="span"
          sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted2, whiteSpace: "nowrap" }}
        >
          {items.length} item{items.length === 1 ? "" : "s"}
          {carried > 0 ? ` · ${carried} carried` : ""}
        </Box>
      </Box>

      {ghost && (
        <Box sx={{ p: "12px 14px", borderBottom: `1px solid ${c.borderLight3}` }}>
          <Box
            data-testid="rail-ghost"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: "11px 12px",
              border: `1px dashed ${c.teal}`,
              background: c.tealTint2,
              borderRadius: `${brainTokens.radius.card}px`,
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: `${brainTokens.radius.chip}px`,
                border: `1.5px dashed ${c.teal}`,
                flex: "none",
                mt: "2px",
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: 14, fontWeight: 500, color: c.tealInk, lineHeight: 1.4 }}>{ghost.text}</Box>
              <Box
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.09em",
                  color: c.tealInk,
                  opacity: 0.8,
                  mt: "5px",
                  whiteSpace: "nowrap",
                }}
              >
                DROP HERE{ghost.ref ? ` · ${ghost.ref}` : ""}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {groups.map((g) => (
        <Box key={g.key} sx={{ borderBottom: `1px solid ${c.borderLight3}`, p: "11px 14px 12px" }}>
          <Box sx={{ px: "4px", pb: "4px" }}>
            <Box
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: c.muted2,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {g.label}
            </Box>
          </Box>
          {g.items.map((f) => {
            const days = carryDays(f, today, today);
            return (
              <Box key={f.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, p: "9px 4px" }}>
                <Box
                  component="button"
                  type="button"
                  role="checkbox"
                  aria-checked={f.done}
                  aria-label={f.done ? `Undo ${f.text}` : `Mark done: ${f.text}`}
                  disabled={busy}
                  onClick={() => onTick(f, !f.done)}
                  sx={{
                    width: 20,
                    height: 20,
                    flex: "none",
                    mt: "2px",
                    borderRadius: `${brainTokens.radius.chip}px`,
                    cursor: busy ? "default" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: f.done ? c.successDot : c.white,
                    color: c.white,
                    fontSize: 12,
                    lineHeight: 1,
                    border: `1.5px solid ${f.done ? c.successDot : c.dropBorder}`,
                  }}
                >
                  {f.done ? "✓" : ""}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 500,
                      lineHeight: 1.4,
                      color: f.done ? c.muted2 : c.ink,
                      textDecoration: f.done ? "line-through" : "none",
                    }}
                  >
                    {f.text}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mt: "3px", flexWrap: "wrap" }}>
                    {(f.registerRef || f.issueKey) && (
                      <Box
                        component="span"
                        sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.muted2, whiteSpace: "nowrap" }}
                      >
                        {f.registerRef || f.issueKey}
                      </Box>
                    )}
                    <CarryChip days={days} />
                  </Box>
                </Box>
                {f.priority === "P1" && <PriorityPill priority="P1" />}
                <Box
                  component="button"
                  type="button"
                  title="Return to the register"
                  aria-label={`Return to the register: ${f.text}`}
                  disabled={busy}
                  onClick={() => onReturn(f)}
                  sx={{
                    width: 24,
                    height: 24,
                    flex: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: `${brainTokens.radius.chip}px`,
                    border: "none",
                    background: "transparent",
                    color: c.placeholder,
                    cursor: busy ? "default" : "pointer",
                    fontSize: 13,
                    "&:hover": { color: c.ink3 },
                  }}
                >
                  ✕
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}

      {items.length === 0 && !ghost && (
        <Box sx={{ p: "30px 20px 34px", textAlign: "center" }}>
          <Box sx={{ fontSize: 15.5, fontWeight: 650, color: c.ink }}>Nothing pulled yet.</Box>
          <Box sx={{ fontSize: 13, color: c.muted, mt: 0.9, lineHeight: 1.6 }}>
            Open a register on the left and add the actions {personName} is doing today.
          </Box>
        </Box>
      )}

      {allDone && (
        <Box sx={{ background: c.successBg, p: "12px 16px", display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: c.successDot, mt: "6px", flex: "none" }} />
          <Box sx={{ fontSize: 13.5, fontWeight: 650, color: c.successText, lineHeight: 1.5 }}>
            Itinerary cleared — {items.length} action{items.length === 1 ? "" : "s"} written back to their
            registers. Nothing carries into tomorrow.
          </Box>
        </Box>
      )}

    </Box>
  );
}
